import { CardElement, CardElementChangeEvent, useRecurly, UseRecurlyInstance } from "@recurly/react-recurly";
import { TokenPayload } from "@recurly/recurly-js";
import { useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  PWSSessionBackend,
  RecurlyBillingInfo,
  RecurlyBillingInfoCreate,
  CANADA_PROVINCES_AND_TERRITORIES,
  COAST_MESSAGES,
  US_STATES_AND_TERRITORIES,
} from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Button, ButtonKind } from "../../../../../../core/ui";
import Heading from "../../../../../../core/ui/Heading.component";
import { axiosErrorReason } from "../../../../lib/coastCommon";
import { FORM_ERRORS, SelectInput, SelectOption, SimpleInput } from "../../../forms";
import { CoastAlert } from "../../common/coast-alert/CoastAlert.model";
import { useCoastAlerts } from "../../common/coast-alert/CoastAlerts.hook";
import { CoastAlertsDisplay } from "../../common/coast-alert/CoastAlertsDisplay.components";
import { BillingInfoFormFields } from "../../common/coastUser.types";
import { UsersButton } from "../../common/UsersButton.components";
import { CoastUserBillingInfoDisplay } from "../CoastUserSettingsBillingInfoDisplay";

const log = ILog.logger("CoastUserUpdateBillingInfo.component");

export interface CoastUserUpdateBillingInfoProps {
  onUpdate: (v: RecurlyBillingInfo) => void;
  billingInfo?: RecurlyBillingInfo | null;
  goBack: (v?: CoastAlert[]) => void;
  fetchingError: CoastAlert | null;
  session: PWSSessionBackend;
}

const getStatesDropdownOptions = (country?: string) =>
  (country === "CA" ? CANADA_PROVINCES_AND_TERRITORIES : US_STATES_AND_TERRITORIES).map(({ label, value }) => (
    <SelectOption label={label} value={value} key={label} />
  ));

function processRecurlyToken(
  recurly: UseRecurlyInstance,
  ref: React.RefObject<HTMLFormElement>,
  isBankAccount: boolean,
) {
  return new Promise<TokenPayload>((resolve, reject) => {
    if (!ref.current) {
      return reject("Error processing ReCurly Payment: missing payment method details");
    } else {
      const accountToken = isBankAccount ? recurly.bankAccount.token : recurly.token;
      accountToken(ref.current, (err, token) => {
        if (err) {
          log.error(`Token generation error: ${err.name}`, err.message);
          return reject(err);
        }
        return resolve(token);
      });
    }
  });
}

export function CoastUserUpdateBillingInfo({
  fetchingError,
  billingInfo,
  onUpdate,
  session,
  goBack,
}: CoastUserUpdateBillingInfoProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const recurly = useRecurly();

  const formMethods = useForm<BillingInfoFormFields>({
    defaultValues: {
      firstName: billingInfo?.firstName,
      lastName: billingInfo?.lastName,
      paymentMethod: billingInfo?.paymentMethod.object,
      routingNumber: billingInfo?.paymentMethod.routingNumber,
      accountType: billingInfo?.paymentMethod.accountType,
      street1: billingInfo?.address.street1,
      street2: billingInfo?.address.street2,
      postalCode: billingInfo?.address.postalCode,
      city: billingInfo?.address.city,
      region: billingInfo?.address.region,
      country: billingInfo?.address.country,
    },
  });

  const { handleSubmit, register, errors, watch } = formMethods;

  const { alerts, setSuccessAlert, setErrorAlert, clearAllAlerts } = useCoastAlerts();
  const [isEditingPayment, setEditingPayment] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);

  const countryWatcher = watch("country");

  useEffect(() => {
    if (
      billingInfo &&
      billingInfo.paymentMethod.object === "bank_account_info" &&
      billingInfo.paymentMethod.nameOnAccount &&
      !billingInfo.firstName &&
      !billingInfo.lastName
    ) {
      const names = billingInfo.paymentMethod.nameOnAccount.split(" ");
      billingInfo.firstName = names[0];
      billingInfo.lastName = names[names.length - 1];
    }
  }, [billingInfo]);

  const handleCardElementEvent = useCallback(
    ({ valid }: CardElementChangeEvent) => {
      if (valid !== isCardValid) {
        setIsCardValid(valid);
      }
    },
    [isCardValid],
  );

  const editPayment = useCallback(() => {
    setEditingPayment(true);
    clearAllAlerts();
  }, [clearAllAlerts]);

  const createRequestBody = useCallback(
    async ({ firstName, lastName, paymentMethod, ...values }: BillingInfoFormFields) => {
      const body: { billingInfo: RecurlyBillingInfoCreate } = { billingInfo: {} };

      if (isEditingPayment) {
        try {
          body.billingInfo = {
            tokenId: (await processRecurlyToken(recurly, formRef, paymentMethod === "bank_account_info")).id,
          };
        } catch (e) {
          log.error(e);
          setErrorAlert();
          return setIsProcessing(false);
        }
      } else {
        body.billingInfo = {
          ...(paymentMethod === "bank_account_info" && { nameOnAccount: `${firstName} ${lastName}` }),
          firstName,
          lastName,
          address: {
            street1: values.street1,
            street2: values.street2,
            city: values.city,
            country: values.country,
            postalCode: values.postalCode,
            region: values.region,
          },
        };
      }

      return body;
    },
    [isEditingPayment, recurly, setErrorAlert],
  );

  const onSubmit: SubmitHandler<BillingInfoFormFields> = useCallback(
    async (billingInfoValues, event) => {
      event?.preventDefault();
      clearAllAlerts();

      if (!session.state.accessToken) {
        return setErrorAlert(COAST_MESSAGES.MISSING_ACCESS_TOKEN);
      }

      setIsProcessing(true);

      const requestBody = await createRequestBody(billingInfoValues);
      if (!requestBody) {
        setIsProcessing(false);
        return;
      }

      try {
        const resp = await session.memberService.updateBillingInfo(session.state.accessToken, requestBody);
        onUpdate(resp.billingInfo);
        setSuccessAlert();
      } catch (e) {
        setErrorAlert(axiosErrorReason(e));
      } finally {
        setIsProcessing(false);
      }
    },
    [
      clearAllAlerts,
      createRequestBody,
      onUpdate,
      session.memberService,
      session.state.accessToken,
      setErrorAlert,
      setSuccessAlert,
    ],
  );

  return (
    <div className="component-coast-account-change-billing">
      <Button className="button-back-to-account" kind={ButtonKind.CUSTOMIZED} click={() => goBack()}>
        Back
      </Button>

      <section className="account-sub-section">
        <CoastAlertsDisplay alerts={alerts} />

        <Heading level={2}>Update Billing Info</Heading>

        <p>Need to change your billing info? Enter your new billing info below</p>

        <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
          <div className="spaced-out-block">
            <hr className="divider" />
          </div>
          {isEditingPayment ? (
            <React.Fragment>
              <CardElement onChange={handleCardElementEvent} />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <CoastUserBillingInfoDisplay
                fetchingError={fetchingError}
                billingInfo={billingInfo}
                onEditClick={editPayment}
                isSubMenu={false}
              />
            </React.Fragment>
          )}

          <div className="spaced-out-block">
            <hr className="divider" />
          </div>

          <div className="account-form-row">
            <SimpleInput
              placeholder="Enter first name"
              rules={{ required: FORM_ERRORS.required }}
              error={errors.firstName}
              register={register}
              label="First Name"
              name="firstName"
              dataRecurly="first_name"
              type="text"
              className="column-6"
            />

            <SimpleInput
              placeholder="Enter last name"
              rules={{ required: FORM_ERRORS.required }}
              error={errors.lastName}
              register={register}
              label="Last Name"
              name="lastName"
              dataRecurly="last_name"
              type="text"
              className="column-6"
            />
            <input name="nameOnAccount" ref={register} data-recurly="name_on_account" hidden={true} type="text" />
          </div>

          <SimpleInput
            placeholder="Enter billing address"
            rules={{ required: FORM_ERRORS.required }}
            error={errors.street1}
            register={register}
            label="Billing Address"
            name="street1"
            dataRecurly="address1"
            type="text"
          />

          <SimpleInput error={errors.street2} register={register} name="street2" dataRecurly="address2" type="text" />
          <div className="account-form-row">
            <SimpleInput
              placeholder="Enter your city"
              rules={{ required: FORM_ERRORS.required }}
              error={errors.city}
              register={register}
              label="City"
              name="city"
              dataRecurly="city"
              type="text"
              className="column-4"
            />

            <SelectInput
              register={register}
              rules={{ required: FORM_ERRORS.required }}
              error={errors.region}
              placeholder={{ label: "Select...", value: "" }}
              label={countryWatcher === "CA" ? "Province" : "State"}
              className="column-4"
              name="region"
              dataRecurly="state"
            >
              {getStatesDropdownOptions(countryWatcher)}
            </SelectInput>

            <SimpleInput
              placeholder={countryWatcher === "CA" ? "Postal Code" : "Zip Code"}
              rules={{ required: FORM_ERRORS.required }}
              error={errors.postalCode}
              register={register}
              type="text"
              label={countryWatcher === "CA" ? "Postal Code" : "Zip Code"}
              name="postalCode"
              dataRecurly="postal_code"
              className="column-4"
              size={10}
            />
          </div>

          <UsersButton
            disabled={!!Object.keys(errors).length || isProcessing || (isEditingPayment && !isCardValid)}
            isProcessing={isProcessing}
            label="Update billing"
            type="submit"
          >
            Update
          </UsersButton>
        </form>
      </section>
    </div>
  );
}
